import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ApprovalCard = ({ document, onReview, onSign, onDelegate }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-warning bg-warning/10 border-warning/20';
      case 'in-progress': return 'text-primary bg-primary/10 border-primary/20';
      case 'completed': return 'text-success bg-success/10 border-success/20';
      case 'overdue': return 'text-destructive bg-destructive/10 border-destructive/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getProgressPercentage = () => {
    const completed = document?.signers?.filter(s => s?.status === 'signed')?.length;
    return (completed / document?.signers?.length) * 100;
  };

  const formatDeadline = (date) => {
    const now = new Date();
    const deadline = new Date(date);
    const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-2 nav-transition">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Icon 
              name={document?.type === 'BPD' ? 'FileText' : 'FileCode'} 
              size={20} 
              className="text-primary"
            />
            <h3 className="text-lg font-semibold text-foreground">
              {document?.title}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {document?.description}
          </p>
          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
            <span>Version {document?.version}</span>
            <span>•</span>
            <span>Created {new Date(document.createdAt)?.toLocaleDateString()}</span>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(document?.status)}`}>
          {document?.status?.charAt(0)?.toUpperCase() + document?.status?.slice(1)}
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            Approval Progress
          </span>
          <span className="text-sm text-muted-foreground">
            {document?.signers?.filter(s => s?.status === 'signed')?.length} of {document?.signers?.length} signed
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full nav-transition"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
      </div>
      {/* Signers */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Signers</h4>
        <div className="space-y-2">
          {document?.signers?.map((signer, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-medium">
                  {signer?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {signer?.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {signer?.role}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {signer?.status === 'signed' && (
                  <>
                    <Icon name="CheckCircle2" size={16} className="text-success" />
                    <span className="text-xs text-muted-foreground">
                      {new Date(signer.signedAt)?.toLocaleDateString()}
                    </span>
                  </>
                )}
                {signer?.status === 'pending' && (
                  <Icon name="Clock" size={16} className="text-warning" />
                )}
                {signer?.status === 'delegated' && (
                  <Icon name="UserCheck" size={16} className="text-primary" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Deadline */}
      <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-muted-foreground" />
          <span className="text-sm text-foreground">Deadline</span>
        </div>
        <span className={`text-sm font-medium ${
          document?.status === 'overdue' ? 'text-destructive' : 'text-foreground'
        }`}>
          {formatDeadline(document?.deadline)}
        </span>
      </div>
      {/* Actions */}
      <div className="flex items-center space-x-3">
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={() => onReview(document)}
        >
          Review
        </Button>
        {document?.canSign && (
          <Button
            variant="default"
            size="sm"
            iconName="PenTool"
            iconPosition="left"
            onClick={() => onSign(document)}
          >
            Sign
          </Button>
        )}
        {document?.canDelegate && (
          <Button
            variant="ghost"
            size="sm"
            iconName="UserCheck"
            iconPosition="left"
            onClick={() => onDelegate(document)}
          >
            Delegate
          </Button>
        )}
      </div>
    </div>
  );
};

export default ApprovalCard;